import { differenceInDays } from 'date-fns';

// https://flaviocopes.com/how-to-determine-date-is-today-javascript/
export const isToday = (date: Date): boolean => {
    const today: Date = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
};

// https://stackoverflow.com/questions/36787908/how-to-check-if-date-is-in-this-week-in-javascript
export const isDateInThisWeek = (date: Date): boolean => {
    const todayObj: Date = new Date();
    const todayDate: number = todayObj.getDate();
    const todayDay: number = todayObj.getDay();

    const firstDayOfWeek: Date = new Date(todayObj.setDate(todayDate - todayDay));

    const lastDayOfWeek: Date = new Date(firstDayOfWeek);
    lastDayOfWeek.setDate(lastDayOfWeek.getDate() + 6);

    return date >= firstDayOfWeek && date <= lastDayOfWeek;
};

export const getDayOfTheWeek = (date: Date): string => {
    const weekday: string[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return weekday[date.getDay()];
};

export const isDateInThisWeekButNotToday = (date: Date): boolean => {
    const todayObj: Date = new Date();
    const todayDate: number = todayObj.getDate();
    const todayDay: number = todayObj.getDay();

    const firstDayOfWeek: Date = new Date(todayObj.setDate(todayDate - todayDay));

    const lastDayOfWeek: Date = new Date(firstDayOfWeek);
    lastDayOfWeek.setDate(lastDayOfWeek.getDate() + 6);

    return date > todayObj && date <= lastDayOfWeek;
};

export const isDateInThisMonth = (date: Date): boolean => {
    const today: Date = new Date();
    return date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
};

export const isDateInThisYear = (date: Date): boolean => {
    const today: Date = new Date();
    return date.getFullYear() === today.getFullYear();
};

export const isNowMorning = (): boolean => {
    const date: Date = new Date();
    return date.getHours() < 12 || (date.getHours() === 12 && date.getMinutes() < 1);
};

export const parseDate = (s: string): Date => {
    const b: string[] = s.split(/\D/);
    return new Date(+b[0], +b[1] - 1, +b[2]);
};

export const parseDDMMYYYToDate = (s: string): Date => {
    const b: string[] = s.split(/\D/);
    return new Date(+b[2], +b[1] - 1, +b[0]);
};

export const formatDateDDMMYYYY = (d: Date, separator?: string): string => {
    d = new Date(d);
    let month: string = '' + (d.getMonth() + 1);
    let day: string = '' + d.getDate();
    const year: number = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [day, month, year].join(separator ? separator : '-');
};

export const formatDateYYYYMMDD = (d: Date): string => {
    d = new Date(d);
    let month: string = '' + (d.getMonth() + 1);
    let day: string = '' + d.getDate();
    const year: number = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
};

export const formatDateYYYYMMDDtoDDMMYYYY = (date: string): string => {
    const b: string[] = date.split('-');
    return [b[2], b[1], b[0]].join('-');
};

export const getFirstMondayOfMonth = (d: Date): Date => {
    d.setDate(1);
    while (d.getDay() !== 1) {
        d.setDate(d.getDate() + 1);
    }
    return d;
};

export const isDate1BeforeDate2 = (d1: Date, d2: Date): boolean => d1.getTime() < d2.getTime();
export const isDate1AfterDate2 = (d1: Date, d2: Date): boolean => d1.getTime() > d2.getTime();

export const getFirstDayOfSchoolYear = (): Date => {
    const now: Date = new Date();
    return new Date(now.getMonth() < 7 ? now.getFullYear() - 1 : now.getFullYear(), 7, 1);
};

export const getFirstDayOfScholarYear = (): Date => {
    const now: Date = new Date();
    return new Date(now.getMonth() < 6 ? now.getFullYear() - 1 : now.getFullYear(), 7, 1);
};

export const getLastDayOfSchoolYear = (): Date => {
    const now: Date = new Date();
    return new Date(now.getMonth() < 7 ? now.getFullYear() : now.getFullYear() + 1, 6, 31);
};

export const getFirstDayOfNextSchoolYear = (): Date => {
    const now: Date = new Date();
    return new Date(now.getMonth() < 7 ? now.getFullYear() : now.getFullYear() + 1, 7, 1);
};

/** Exclusive end of school year (1 July) — last included day is 30 June */
export const getFirstDayOfJuly = (): Date => {
    const now: Date = new Date();
    return new Date(now.getMonth() < 6 ? now.getFullYear() : now.getFullYear() + 1, 8, 1);
};

export const getNumberOfDaysScholarYear = (): number => differenceInDays(getFirstDayOfNextSchoolYear(), getFirstDayOfSchoolYear());

export const getLastDayOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth() + 1, 1);

export const getMonthTrad = (m: number): string => {
    switch (m) {
        case 0:
            return 'MONTH.JAN';
        case 1:
            return 'MONTH.FEV';
        case 2:
            return 'MONTH.MAR';
        case 3:
            return 'MONTH.AVR';
        case 4:
            return 'MONTH.MAI';
        case 5:
            return 'MONTH.JUN';
        case 6:
            return 'MONTH.JUL';
        case 7:
            return 'MONTH.AOU';
        case 8:
            return 'MONTH.SEP';
        case 9:
            return 'MONTH.OCT';
        case 10:
            return 'MONTH.NOV';
        case 11:
            return 'MONTH.DEC';
    }
    return '';
};

export const getNowHours = (): string => {
    const now: Date = new Date();
    const hours: number | string = now.getHours() < 10 ? '0' + now.getHours() : now.getHours();
    const min: number | string = now.getMinutes() < 10 ? '0' + now.getMinutes() : now.getMinutes();
    return hours + ':' + min;
};

/**
 * Get firt day of month dd/MM/YYYY
 *
 * @param month jan = 0, sept = 8
 * @returns first day
 */
export const getStartDateOfMonth = (month: number, schoolHasSwitchToNextYear?: boolean): string => {
    const today: Date = new Date();
    const currentMonth: number = today.getMonth();
    const year: number = getYear(currentMonth, month, schoolHasSwitchToNextYear);
    return year + '-' + (month + 1) + '-01';
};

/**
 * Get Last dat of month dd/MM/YYYY
 *
 * @param month jan = 0
 * @returns last day
 */
export const getEndDateOfMonth = (month: number, schoolHasSwitchToNextYear?: boolean): string => {
    const today: Date = new Date();
    const currentMonth: number = today.getMonth();
    const year: number = getYear(currentMonth, month, schoolHasSwitchToNextYear);
    const day: number = new Date(year, month + 1, 0).getDate();
    return year + '-' + (month + 1) + '-' + day;
};

export const isDateRangeValid = (start: string, end: string): boolean => {
    if (start && end) {
        return new Date(end).getTime() > new Date(start).getTime();
    }
    return false;
};

const getYear = (currentMonth: number, displayedMonth: number, schoolHasSwitchToNextYear: boolean): number => {
    const today: Date = new Date();
    switch (true) {
        case currentMonth >= 7 && displayedMonth >= 7:
            return today.getFullYear();
        case currentMonth >= 7 && displayedMonth < 7:
            return today.getFullYear() + 1;
        case schoolHasSwitchToNextYear && currentMonth < 7 && displayedMonth >= 7:
            return today.getFullYear();
        case schoolHasSwitchToNextYear && currentMonth < 7 && displayedMonth < 7:
            return today.getFullYear() + 1;
        case currentMonth < 7 && displayedMonth < 7:
            return today.getFullYear();
        case currentMonth < 7 && displayedMonth >= 7:
            return today.getFullYear() - 1;
        default:
            return today.getFullYear();
    }
};
